import { FormFieldContainer, FormLabel, FormTextAreaInput } from './FormComponents';
import { ConditionalValidationErrorMessage } from './ValidationErrorMessage';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import strings from '../../strings/strings.json';
import { BodyRegular, Flex, FlexCenter } from 'components/Elements';
import useWindowWidth from 'hooks/useWindowWidth';
import ErrorIcon from '../../assets/icons/indicators/ErrorIcon';
import { ComponentPropsWithoutRef } from 'react';
import React from 'react';

type TextAreaFieldProps = {
    fieldName: string;
    required?: boolean;
    aria: string;
    errorMessage?: string;
    charactersLeft?: number;
    unfocusable?: boolean;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    height?: number;
} & ComponentPropsWithoutRef<'textarea'>;

const TextAreaField = React.forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
    (
        {
            fieldName,
            required,
            aria,
            errorMessage = '',
            charactersLeft,
            unfocusable,
            ariaLabelledBy,
            ariaDescribedBy,
            height,
            ...props
        },
        ref,
    ) => {
        const { currentWindowWidth } = useWindowWidth();

        return (
            <FormFieldContainer>
                <FormLabel id={`${fieldName} ${strings.textAreaField.formLabel}`} htmlFor={fieldName}>
                    {fieldName}
                    {required && ' *'}
                </FormLabel>
                <FormTextAreaInput
                    ref={ref}
                    id={fieldName}
                    tabIndex={unfocusable ? -1 : 0}
                    aria-label={aria}
                    $error={!!errorMessage}
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={ariaDescribedBy}
                    style={{ height: `${height}px` }}
                    {...props}
                />
                {charactersLeft !== 0 ? (
                    <TextLimitContainer>
                        <BodySmallContainer $bold>{strings.textAreaField.formTextAreaLimit}: {charactersLeft}</BodySmallContainer>
                    </TextLimitContainer>
                ) : (
                    <TextLimitContainer>
                        <TextAreaLimitErrorContainer>
                            <ErrorIcon />
                            {currentWindowWidth > parseInt(breakpoints.sm) && (
                                <BodySmallContainer $errorColour $bold>
                                    {strings.textAreaField.formTextAreaLimitError}
                                </BodySmallContainer>
                            )}
                        </TextAreaLimitErrorContainer>
                        <BodySmallContainer $errorColour $bold>
                            {charactersLeft}
                        </BodySmallContainer>
                    </TextLimitContainer>
                )}
                {errorMessage && <ConditionalValidationErrorMessage error={errorMessage} />}
            </FormFieldContainer>
        );
    },
);

TextAreaField.displayName = 'TextAreaField';

export default TextAreaField;

const TextLimitContainer = styled(Flex)`
    padding-top: ${sizes.spacingSm};
`;

const BodySmallContainer = styled(BodyRegular)<{ $errorColour?: boolean }>`
    color: ${({ $errorColour }) => {
        return $errorColour ? theme.errorColour : theme.textColour;
    }};
`;

const TextAreaLimitErrorContainer = styled(FlexCenter)`
    gap: ${sizes.spacingSm};
`;
